import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, throwError, of, map } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment as appEnvironment } from '../../../environments/environment';
import { Environment, EnvironmentConfig, ENVIRONMENT_CONFIG } from '../models/environment.model';
import { parseMessageContent } from '../utils/message-parser.util';
import { ApiResponse, SessionsResponse } from '../../pages/conversation-viewer/models/conversation-visualizer.model';
import { AuthService } from './auth.service';
import { ChatbotManagementService } from './chatbot-management.service';
import { TraceFormatterService } from './trace-formatter.service';

export interface SessionHeader {
  sessionId: string;
  conversationId: string;
  startedAt: string;
  lastActivity: string;
  phoneNumber?: string;
  currentIntent?: string;
  sessionSource?: string;
  wasTransfered: boolean;
  messageCount?: number;
}

export interface ConversationSession extends SessionHeader {
  messages: ConversationMessage[];
}

export interface ConversationMessage {
  messageId: string;
  role: string;
  text: string;
  timestamp: string;
  intent?: string;
  agentName?: string;
  isGreeting?: boolean;
  isFinalAnswer?: boolean;
  agentTrace?: any;
  answeredMessageId?: string;
  state?: string;
  retrievedArticles?: any[];
}

// Response for list sessions endpoint
export interface SessionsListResponse {
  sessions: SessionHeader[];
  count: number;
  hasMore: boolean;
  nextToken?: string;
}

// Response for get session endpoint
export interface SessionDetailResponse {
  session: ConversationSession;
}

// Legacy response format for backwards compatibility
export interface ConversationResponse {
  conversationId: string;
  sessions: ConversationSession[];
  serviceName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConversationVisualizerService {
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService, { optional: true });
  private readonly envConfig = inject(ENVIRONMENT_CONFIG, {
    optional: true,
  }) as EnvironmentConfig | null;


  private readonly authService = inject(AuthService);
  private readonly chatbotService = inject(ChatbotManagementService);
  private readonly traceFormatter = inject(TraceFormatterService);
  private readonly baseUrl = appEnvironment.baseApiUrl;
  private readonly chatbotsEndpoint = `${this.baseUrl}/chatbotconversations/run`;

  // ============================================
  // NEW API METHODS - Generic Query Endpoint
  // ============================================
  runChatbotConversationQuery(
    chatbotCode: string,
    conversationParams: Record<string, any>,
    environment: Environment,
    nextToken?: string
  ): Observable<SessionsResponse> {

    // Build request body
    const body = {
      chatbotCode,
      conversationParams: {
        ...conversationParams,
        ...(nextToken ? { nextToken } : {})
      }
    };

    // Get auth headers
    const headers = this.getAuthHeaders();
        
    return this.http.post<ApiResponse>(this.chatbotsEndpoint, body, { headers }).pipe(
      map(response => {
        if (response.status === 'ERROR') {
          throw new Error(typeof response.responseData === 'string' ? response.responseData : 'Error desconocido');
        }

        // Si llega una sola sesión, backend retorna session
        if (
          typeof response.responseData === 'object' &&
          response.responseData !== null &&
          'session' in response.responseData
        ) {
          const session = response.responseData.session;
          return {
            sessions: [session],
            count: 1,
            hasMore: false,
            nextToken: undefined
          };
        }

        // Si llega más de una sesión, backend retorna sessions
        const data = response.responseData as {
          sessions: any[];
          count: number;
          hasMore: boolean;
          nextToken?: string;
        };

        return {
          sessions: data.sessions || [],
          count: data.count || 0,
          hasMore: data.hasMore || false,
          nextToken: data.nextToken
        };
      }),
      catchError(error => {
        console.error('Error in runChatbotConversationQuery:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get a complete session with all messages
   * GET /session?conversationId=xxx&sessionId=yyy
   */
  getSession(
    sessionId: string,
    chatbotCode: string | null = null
  ): Observable<SessionDetailResponse> {

    const headers = this.authService.getAuthHeaders();
    const body = {
      chatbotCode,
      conversationParams: {
        sessionId
      }
    };

    return this.http.post<ApiResponse>(this.chatbotsEndpoint, body, { headers }).pipe(
      map(response => {
        if (response.status === 'ERROR') {
          throw new Error(typeof response.responseData === 'string' ? response.responseData : 'Error desconocido');
        }

        // Si llega una sola sesión, backend retorna session
        if (
          typeof response.responseData === 'object' &&
          response.responseData !== null &&
          'session' in response.responseData
        ) {
          return {
            session: response.responseData.session
          };
        }

        // Si llega más de una sesión, backend retorna sessions - tomar la primera
        const data = response.responseData as {
          sessions: any[];
          count: number;
          hasMore: boolean;
          nextToken?: string;
        };

        if (!data.sessions || data.sessions.length === 0) {
          throw new Error('No se encontró la sesión solicitada');
        }

        return {
          session: data.sessions[0]
        };
      }),
      catchError(error => {
        console.error('Error in getSession:', error);
        return throwError(() => error);
      })
    );
  }


  private getMockSession(conversationId: string, sessionId: string): Observable<SessionDetailResponse> {
    const mockSession: ConversationSession = {
      sessionId,
      conversationId,
      startedAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      phoneNumber: '+56912345678',
      currentIntent: 'greeting',
      sessionSource: 'whatsapp',
      wasTransfered: false,
      messageCount: 3,
      messages: [
        {
          messageId: 'msg-1',
          role: 'user',
          text: 'Hello',
          timestamp: (Date.now() / 1000 - 60).toString(),
        },
        {
          messageId: 'msg-2',
          role: 'assistant',
          text: 'Hello! How can I help you today?',
          timestamp: (Date.now() / 1000 - 30).toString(),
          agentName: 'Mock Agent',
        },
        {
          messageId: 'msg-3',
          role: 'user',
          text: 'I need support',
          timestamp: (Date.now() / 1000).toString(),
        },
      ],
    };
    return of({ session: mockSession });
  }



  /**
   * Extracts retrievedArticles from agentTrace
   * Looks for articlesRetrieval events or tool_output with knowledge base response
   */
  private extractArticlesFromTrace(agentTrace: any, messageId: string, answeredMessageId?: string): any[] | undefined {
    if (!agentTrace) {
      return undefined;
    }
    
    try {
      const traces = this.parseAgentTrace(agentTrace);
      if (!traces) {
        return undefined;
      }
      
      const articles = this.collectArticlesFromTraces(traces, messageId, answeredMessageId);
      return articles.length > 0 ? articles : undefined;
    } catch (error) {
      console.error('Error parsing agent trace:', error);
      return undefined;
    }
  }

  /**
   * Parses agent trace to array format
   */
  private parseAgentTrace(agentTrace: any): any[] | null {
    const traces = typeof agentTrace === 'string' ? JSON.parse(agentTrace) : agentTrace;
    return Array.isArray(traces) ? traces : null;
  }

  /**
   * Collects articles from all agent traces
   */
  private collectArticlesFromTraces(traces: any[], messageId: string, answeredMessageId?: string): any[] {
    const articles: any[] = [];
    
    for (const agentData of traces) {
      if (!agentData.traces || !Array.isArray(agentData.traces)) {
        continue;
      }
      
      const agentArticles = this.extractArticlesFromAgentTraces(agentData.traces, messageId, answeredMessageId);
      articles.push(...agentArticles);
    }
    
    return articles;
  }

  /**
   * Extracts articles from a single agent's traces
   */
  private extractArticlesFromAgentTraces(traces: any[], messageId: string, answeredMessageId?: string): any[] {
    const articles: any[] = [];
    
    for (const trace of traces) {
      const article = this.extractArticleFromTrace(trace, messageId, answeredMessageId);
      if (article) {
        articles.push(article);
      }
    }
    
    return articles;
  }

  /**
   * Extracts article from a single trace
   */
  private extractArticleFromTrace(trace: any, messageId: string, answeredMessageId?: string): Record<string, any> | null {
    // WebSocket format
    if (trace.type === 'articlesRetrieval' && trace.body?.articles) {
      return trace.body;
    }
    
    // Historical format - tool_output with knowledge base
    if (trace.type === 'tool_output' && trace.body?.text) {
      return this.parseKnowledgeBaseFromToolOutput(trace.body.text, messageId, answeredMessageId);
    }
    
    return null;
  }

  /**
   * Parses knowledge base XML from tool_output text and converts to ArticleNotification format
   */
  private parseKnowledgeBaseFromToolOutput(toolOutputText: string, messageId: string, answeredMessageId?: string): Record<string, any> | null {
    try {      
      // Extract XML content between <knowledge_base_response> tags
      const knowledgeBaseRegex = /<knowledge_base_response>([\s\S]*?)<\/knowledge_base_response>/;
      const xmlMatch = knowledgeBaseRegex.exec(toolOutputText);
      if (!xmlMatch) {
        return null;
      }

      const xmlContent = xmlMatch[1];
      
      // Parse articles from XML
      const articleMatches = xmlContent.matchAll(/<article>([\s\S]*?)<\/article>/g);
      const articles: any[] = [];
      
      for (const match of articleMatches) {
        const articleXml = match[1];
        
        // Extract fields using regex
        const mainCategoryRegex = /<main_category>([\s\S]*?)<\/main_category>/;
        const subCategoryRegex = /<sub_category>([\s\S]*?)<\/sub_category>/;
        const questionRegex = /<question>([\s\S]*?)<\/question>/;
        const responseRegex = /<response>([\s\S]*?)<\/response>/;
        const orderRegex = /<order>([\s\S]*?)<\/order>/;
        
        const mainCategory = mainCategoryRegex.exec(articleXml)?.[1]?.trim() || '';
        const subCategory = subCategoryRegex.exec(articleXml)?.[1]?.trim() || '';
        const question = questionRegex.exec(articleXml)?.[1]?.trim() || '';
        const response = responseRegex.exec(articleXml)?.[1]?.trim() || '';
        const order = orderRegex.exec(articleXml)?.[1]?.trim() || '';
        
        articles.push({
          identifier: `kb-article-${order}`,
          documentQuestion: question,
          documentContent: response,
          category1: mainCategory,
          category2: subCategory,
          similarityScore: 1 - (Number.parseInt(order, 10) - 1) * 0.05, // Approximate score based on order
          action: 'display',
          requestKey: `req-${messageId}-${order}`
        });
      }
      
      if (articles.length === 0) {
        return null;
      }
      
      // Create ArticleNotification format
      const result = {
        sessionId: messageId.split('_')[0] || '',
        messageId: answeredMessageId || messageId,
        userQuestion: '', // Not available in tool_output format
        articles: articles,
        discardedArticles: []
      };
      return result;

    } catch (error) {
      console.error('Error parsing knowledge base XML:', error);
      return null;
    }
  }

  /**
   * Converts ConversationMessage to Message format used by chat components
   */
  convertToMessage(conversationMessage: ConversationMessage): any {    
    const { content, buttons, selectList } = parseMessageContent(conversationMessage.text);
    
    // Normalize agentTrace: handles both raw API format and already-processed display format
    const transformedTrace = this.traceFormatter.normalizeAgentTrace(
      conversationMessage.agentTrace,
      conversationMessage.agentName
    );
    
    // Extract articles from transformed trace
    let retrievedArticles = conversationMessage.retrievedArticles;
    
    if (!retrievedArticles && transformedTrace) {
      retrievedArticles = this.extractArticlesFromTrace(
        transformedTrace, 
        conversationMessage.messageId,
        conversationMessage.answeredMessageId
      );
    }
    
    return {
      id: conversationMessage.messageId,
      content: content,
      isUser: conversationMessage.role === 'user',
      timestamp: new Date(Number.parseFloat(conversationMessage.timestamp) * 1000),
      answeredMessageId: conversationMessage.answeredMessageId,
      agentTrace: transformedTrace ? JSON.stringify(transformedTrace) : undefined,
      intent: conversationMessage.intent,
      agentName: conversationMessage.agentName,
      state: conversationMessage.state,
      buttons: buttons,
      selectList: selectList,
      retrievedArticles: retrievedArticles
    };
  }

  /**
   * Converts a full conversation to Message array
   */
  convertConversationToMessages(conversation: ConversationResponse): any[] {
    const messages: any[] = [];
    
    conversation.sessions.forEach(session => {
      session.messages.forEach(msg => {
        messages.push(this.convertToMessage(msg));
      });
    });

    // Sort by timestamp
    messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    return messages;
  }

  /**
   * Get authorization headers with OAuth token
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.oauthService?.getAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }
}
